<script setup>
/**
 * The priority cell as a control. Writes through `assignPriority`, the same
 * action the conversation sidebar's priority picker uses, so the row and the
 * open thread never disagree.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import PriorityMark from 'dashboard/components-next/Conversation/PriorityMark.vue';
import CellMenu from './CellMenu.vue';
import CellMenuItem from './CellMenuItem.vue';
import { PRIORITY, PRIORITY_META } from 'dashboard/helper/conversationLens';

const props = defineProps({
  conversationId: { type: Number, required: true },
  priority: { type: String, default: null },
});

const { t } = useI18n();
const store = useStore();

// A null priority is a real value the API accepts, so it leads the list the
// way the sidebar's picker does.
const options = computed(() => [
  {
    value: null,
    label: t('TICKETS.PRIORITY.NONE'),
    icon: 'i-lucide-minus',
    iconClass: 'text-n-slate-10',
  },
  ...Object.values(PRIORITY).map(value => ({
    value,
    label: t(PRIORITY_META[value].labelKey),
    icon: PRIORITY_META[value].icon,
    iconClass: PRIORITY_META[value].toneClass,
  })),
]);

const setPriority = async (priority, hide) => {
  hide();
  if (priority === props.priority) return;
  try {
    await store.dispatch('assignPriority', {
      conversationId: props.conversationId,
      priority,
    });
  } catch (error) {
    useAlert(t('TICKETS.TABLE.CELL.ERROR'));
  }
};
</script>

<template>
  <CellMenu>
    <template #trigger="{ isOpen }">
      <button
        type="button"
        data-testid="priority-trigger"
        aria-haspopup="menu"
        :aria-expanded="isOpen"
        :aria-label="t('TICKETS.TABLE.CELL.CHANGE_PRIORITY')"
        class="inline-flex items-center max-w-full gap-1 px-1.5 py-1 rounded-md whitespace-nowrap hover:bg-n-alpha-2"
        :class="isOpen ? 'bg-n-alpha-2' : ''"
      >
        <PriorityMark :priority="priority" show-label />
        <span
          class="i-lucide-chevron-down size-3 shrink-0 text-n-slate-10 opacity-0 group-hover/row:opacity-100"
          :class="isOpen ? 'opacity-100' : ''"
        />
      </button>
    </template>
    <template #default="{ hide }">
      <CellMenuItem
        v-for="option in options"
        :key="String(option.value)"
        role="menuitemradio"
        :data-testid="`priority-option-${option.value}`"
        :label="option.label"
        :icon="option.icon"
        :icon-class="option.iconClass"
        :selected="option.value === priority"
        @click="setPriority(option.value, hide)"
      />
    </template>
  </CellMenu>
</template>
